import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useLanguage } from '@/App';
import { useTranslation } from '@/hooks/useTranslation';
import axios from 'axios';
import { prefetchApi } from '@/lib/apiCache';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Home,
  Inbox,
  BarChart3,
  Settings,
  LogOut,
  Zap,
  Bot,
  X,
  Package,
  FileSpreadsheet,
  ShoppingBag,
  Users,
  Radio,
  ChevronDown,
  ChevronRight,
  User,
  Truck,
  Download,
  CreditCard,
  Bell,
  FileText,
  Moon,
  Sun,
  Crown,
  Globe,
  MessageSquarePlus,
  DollarSign,
  MessageCircleQuestion,
  Ticket
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Auto-expand settings if on a settings page
  useEffect(() => {
    if (location.pathname.startsWith('/settings')) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/conversations/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUnreadCount(response.data.count);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  useEffect(() => {
    // Fetch unread conversation count
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Warm the API cache before the user clicks, so the next page renders instantly
  const prefetchFor = (path) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const uid = user?.id;
    const map = {
      '/dashboard': ['/api/plan/limits', '/api/chatbots'],
      '/inbox': ['/api/conversations/unread-count', '/api/channels', '/api/chatbots'],
      '/agents': ['/api/chatbots', '/api/plan/limits'],
      '/orders': uid ? [`/api/orders/stats?user_id=${uid}`, `/api/products/list?user_id=${uid}`, `/api/payment-methods?user_id=${uid}`] : [],
      '/customers': uid ? [`/api/customers/stats?user_id=${uid}`] : [],
      '/channels': ['/api/chatbots', '/api/channels', '/api/messenger/status', '/api/instagram/status'],
      '/financial': ['/api/financial/report?period=month'],
      '/settings/subscription': ['/api/plan/limits', '/api/payment/duitku/topup/packs', '/api/payment/duitku/topup/balance'],
    };
    (map[path] || []).forEach((u) => prefetchApi(axios, `${BACKEND_URL}${u}`, headers));
  };

  const isActive = (path) => {
    // Menu "Produk Digital" mencakup halaman produk digital + gudang file/kode.
    if (path === '/digital/products') return location.pathname.startsWith('/digital');
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isSettingsActive = () => {
    return location.pathname.startsWith('/settings');
  };

  const navItems = [
    {
      path: '/dashboard',
      icon: Home,
      label: t('sidebar.dashboard')
    },
    {
      path: '/inbox',
      icon: Inbox,
      label: t('sidebar.inbox'),
      badge: unreadCount
    },
    {
      path: '/agents',
      icon: Bot,
      label: 'Agents'
    },
    {
      path: '/orders',
      icon: ShoppingBag,
      label: t('sidebar.orders')
    },
    // Menu produk mengikuti mode toko: mode tunggal = 1 menu, mode "Keduanya" = 2 menu.
    ...(user?.business_type === 'digital'
      ? [{ path: '/digital/products', icon: Download, label: 'Produk Digital' }]
      : user?.business_type === 'both'
        ? [
            { path: '/catalog', icon: Package, label: 'Produk Fisik' },
            { path: '/digital/products', icon: Download, label: 'Produk Digital' },
          ]
        : [{ path: '/catalog', icon: Package, label: 'Produk Fisik' }]),
    {
      path: '/customers',
      icon: Users,
      label: t('sidebar.customers')
    },
    {
      path: '/broadcast',
      icon: Radio,
      label: 'Broadcast'
    },
    // {
    //   path: '/sheets',
    //   icon: FileSpreadsheet,
    //   label: 'Google Sheets'
    // },
    {
      path: '/channels',
      icon: Radio,
      label: t('sidebar.channels')
    },
    {
      path: '/financial',
      icon: DollarSign,
      label: 'Laporan Keuangan'
    },
    {
      path: '/feedback',
      icon: MessageCircleQuestion,
      label: 'Bantuan'
    }
  ];

  const settingsSubItems = [
    { path: '/settings/profile', icon: User, label: t('profile.title') },
    // Toko produk digital tidak pernah menghitung ongkir.
    ...(user?.business_type === 'digital'
      ? []
      : [{ path: '/settings/shipping', icon: Truck, label: t('settings.shipping') || 'Shipping' }]),
    { path: '/settings/payment', icon: CreditCard, label: t('settings.paymentStore') || 'Pembayaran Toko' },
    { path: '/settings/follow-up', icon: MessageSquarePlus, label: 'Follow Up' },
    { path: '/settings/notifications', icon: Bell, label: t('settings.notifications') },
    // { path: '/settings/invoice', icon: FileText, label: t('settings.invoice') || 'Invoice' }, // Hidden: fitur invoice dinonaktifkan sementara
    { path: '/settings/subscription', icon: Crown, label: t('settings.subscription') || 'Langganan' }
  ];

  return (
    <>
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-white dark:bg-zinc-900 border-r border-slate-200 dark:border-zinc-800 z-40 transform transition-transform duration-300 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        {/* Logo - Fixed Header */}
        <div className="p-4 border-b border-slate-200 dark:border-zinc-800 flex justify-between items-center flex-shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/images/logo.png"
              alt="Logo"
              className="w-10 h-10"
            />
            <span className="text-xl font-bold text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
              Nerava
            </span>
          </Link>
          <button 
            onClick={() => setSidebarOpen && setSidebarOpen(false)} 
            className="lg:hidden text-slate-600 dark:text-zinc-300 p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation - Scrollable Middle Section */}
        <nav className="flex-1 overflow-y-auto py-2 min-h-0">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between gap-3 px-4 py-3 transition-colors ${
                  active
                    ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/30 border-l-4 border-indigo-600'
                    : 'text-slate-600 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 border-l-4 border-transparent'
                }`}
                onClick={() => setSidebarOpen && setSidebarOpen(false)}
                onMouseEnter={() => prefetchFor(item.path)}
                onTouchStart={() => prefetchFor(item.path)}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5" />
                  <span>
                    {item.label}
                  </span>
                </div>
                {item.badge > 0 && (
                  <Badge variant="destructive" className="ml-auto">
                    {item.badge > 99 ? '99+' : item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}

          {/* Settings with submenu */}
          <div>
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`w-full flex items-center justify-between gap-3 px-4 py-3 transition-colors ${
                isSettingsActive()
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/30 border-l-4 border-indigo-600'
                  : 'text-slate-600 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 border-l-4 border-transparent'
              }`}
            >
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                <span>{t('sidebar.settings')}</span>
              </div>
              {settingsOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>

            {/* Settings submenu */}
            {settingsOpen && (
              <div className="bg-slate-50 dark:bg-zinc-900/50">
                {settingsSubItems.map((subItem) => {
                  const SubIcon = subItem.icon;
                  const subActive = location.pathname === subItem.path;
                  
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      onMouseEnter={() => prefetchFor(subItem.path)}
                      className={`flex items-center gap-3 pl-12 pr-4 py-2.5 transition-colors text-sm ${
                        subActive
                          ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-100/50 dark:bg-indigo-950/40'
                          : 'text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-zinc-800/50'
                      }`}
                      onClick={() => setSidebarOpen && setSidebarOpen(false)}
                    >
                      <SubIcon className="w-4 h-4" />
                      <span>{subItem.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </nav>

        {/* Bottom Section - Fixed Footer */}
        <div className="border-t border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0">
          <div className="p-3 space-y-1">
              <button
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-slate-600 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors text-sm font-medium dark:bg-zinc-800"
                title={theme === 'dark' ? 'Mode Terang' : 'Mode Gelap'}
              >
                <span className="flex items-center gap-2">
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  <span>{theme === 'dark' ? 'Mode Terang' : 'Mode Gelap'}</span>
                </span>
                <span className={`relative w-9 h-5 rounded-full transition-colors ${theme === 'dark' ? 'bg-indigo-600' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-[left] duration-300 ${theme === 'dark' ? 'left-[18px]' : 'left-0.5'} dark:bg-zinc-900`} />
                </span>
              </button>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors text-sm font-medium dark:bg-red-950/40"
                title={t('sidebar.logout')}
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay - z-30 covers content when sidebar is open */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
          onClick={() => setSidebarOpen && setSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
